"use client";

// Toaster global aplikasi — pembungkus Sonner dengan gaya profesional & konsisten
// brand (rounded, kartu bersih, ikon per-jenis, dukungan light/dark). Dipakai
// sekali di RootLayout menggantikan <Toaster> mentah, sehingga SEMUA notifikasi
// (toast.success/error/warning/info/loading) tampil rapi tanpa mengubah pemanggil.

import { Toaster } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Info, Loader2 } from "lucide-react";

export default function AppToaster() {
  return (
    <>
      <style>{TOASTER_CSS}</style>
      <Toaster
        position="bottom-right"
        closeButton
        gap={10}
        offset={16}
        // Sonner dirender di <body> dengan z 999999, jadi tanpa mobileOffset toast
        // selalu mendarat di atas bar navigasi bawah dan menutupinya 4 detik.
        mobileOffset={{ bottom: "calc(var(--nav-h) + 0.75rem)", left: "12px", right: "12px" }}
        duration={4000}
        // richColors dimatikan: kita kendalikan warna sendiri via [data-type] agar
        // tampil tenang & profesional (aksen di ikon, bukan seluruh latar merah).
        icons={{
          success: <CheckCircle2 className="h-5 w-5" />,
          error: <XCircle className="h-5 w-5" />,
          warning: <AlertTriangle className="h-5 w-5" />,
          info: <Info className="h-5 w-5" />,
          loading: <Loader2 className="h-5 w-5 animate-spin" />,
        }}
        toastOptions={{ className: "gc-toast" }}
      />
    </>
  );
}

const TOASTER_CSS = `
[data-sonner-toaster]{
  --width: 380px;
  font-family: var(--brand-font, inherit);
}
[data-sonner-toast].gc-toast{
  --gc-accent: var(--brand-primary, #2563eb);
  --gc-bg: #ffffff;
  --gc-fg: #0d0d0d;
  --gc-muted: #5c5c66;
  --gc-border: rgba(12,12,14,0.08);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border-radius: 16px;
  background: var(--gc-bg);
  color: var(--gc-fg);
  border: 1px solid var(--gc-border);
  box-shadow: 0 12px 32px rgba(12,12,14,0.12), 0 2px 6px rgba(12,12,14,0.05);
  -webkit-backdrop-filter: blur(12px) saturate(1.4);
  backdrop-filter: blur(12px) saturate(1.4);
}
/* Garis aksen tipis di tepi kiri per jenis notifikasi */
[data-sonner-toast].gc-toast::before{
  content: "";
  position: absolute;
  left: 0; top: 12px; bottom: 12px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--gc-accent);
  opacity: .9;
}
[data-sonner-toast].gc-toast[data-type="success"]{ --gc-accent: #16a34a; }
[data-sonner-toast].gc-toast[data-type="error"]{ --gc-accent: #dc2626; }
[data-sonner-toast].gc-toast[data-type="warning"]{ --gc-accent: #d97706; }
[data-sonner-toast].gc-toast[data-type="info"]{ --gc-accent: var(--brand-primary, #2563eb); }

[data-sonner-toast].gc-toast [data-icon]{
  margin: 1px 0 0 0;
  color: var(--gc-accent);
  flex-shrink: 0;
}
[data-sonner-toast].gc-toast [data-content]{ gap: 2px; }
[data-sonner-toast].gc-toast [data-title]{
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
  letter-spacing: -0.01em;
}
[data-sonner-toast].gc-toast [data-description]{
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--gc-muted);
  margin-top: 2px;
}
/* Tombol aksi (mis. "Coba lagi") */
[data-sonner-toast].gc-toast [data-button]{
  height: 30px;
  border-radius: 9px;
  padding: 0 12px;
  font-weight: 600;
  font-size: 12.5px;
  background: var(--gc-accent);
  color: #fff;
}
[data-sonner-toast].gc-toast [data-button][data-cancel]{
  background: color-mix(in srgb, var(--gc-fg) 8%, transparent);
  color: var(--gc-fg);
}
/* Tombol tutup — halus, muncul jelas saat hover */
[data-sonner-toast].gc-toast [data-close-button]{
  top: 10px;
  left: auto;
  right: 10px;
  width: 20px; height: 20px;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--gc-fg) 6%, transparent);
  border: none;
  color: var(--gc-muted);
  transition: background .15s, color .15s, transform .15s;
}
[data-sonner-toast].gc-toast [data-close-button]:hover{
  background: color-mix(in srgb, var(--gc-fg) 12%, transparent);
  color: var(--gc-fg);
  transform: scale(1.06);
}

@media (prefers-color-scheme: dark){
  [data-sonner-toast].gc-toast{
    --gc-bg: color-mix(in srgb, #16161c 92%, transparent);
    --gc-fg: #f4f4f7;
    --gc-muted: #a1a1ad;
    --gc-border: rgba(255,255,255,0.10);
    box-shadow: 0 14px 36px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
  }
}
`;
